
import React, { useState, useEffect, useRef } from 'react';
import StoreButtons from '../../../components/common/StoreButtons';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { nativeGoogleSignIn, isCapacitor } from '../../../services/googleAuthService';
import { Capacitor } from '@capacitor/core';
import { nativeAppleSignIn } from '../../../services/appleAuthService';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { FacebookLogin as NativeFacebookLogin } from '@capacitor-community/facebook-login';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { authAPI } from '../../services/authApi';
import { getConfig } from '../../../config';
import Logo from '../../../components/common/Logo';
import { Eye, EyeOff } from 'lucide-react';

const RESEND_COOLDOWN_MS = 3 * 60 * 1000;
const MAX_RESEND_ATTEMPTS = 3;
const RESEND_STORAGE_KEY = 'emailVerifyResend';

function isSafeRedirect(url) {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

function getResendState(email) {
  try {
    const stored = JSON.parse(localStorage.getItem(RESEND_STORAGE_KEY) || '{}');
    return stored[email] || { count: 0, lastSentAt: null };
  } catch {
    return { count: 0, lastSentAt: null };
  }
}

function saveResendState(email, state) {
  try {
    const stored = JSON.parse(localStorage.getItem(RESEND_STORAGE_KEY) || '{}');
    stored[email] = state;
    localStorage.setItem(RESEND_STORAGE_KEY, JSON.stringify(stored));
  } catch {}
}


const GoogleButton = ({ onSuccess, onError, buttonWidth, text = 'continue_with' }) => {
  if (isCapacitor) {
    // Native Google Sign-In for iOS — avoids WKWebView iframe/popup restrictions
    const label = text === 'signin_with' ? 'Sign in with Google' : 'Continue with Google';
    return (
      <button
        type="button"
        onClick={async () => {
          try {
            const idToken = await nativeGoogleSignIn();
            onSuccess({ credential: idToken });
          } catch (e) {
            console.error(`[GoogleSignIn] caught in onClick: ${e?.message ?? JSON.stringify(e)} code=${e?.code}`);
            onError(e);
          }
        }}
        className="flex items-center justify-center gap-3 w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
      >
        <svg width="18" height="18" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          <path fill="none" d="M0 0h48v48H0z"/>
        </svg>
        {label}
      </button>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <GoogleLogin
        onSuccess={onSuccess}
        onError={onError}
        text={text}
        width={buttonWidth}
        useOneTap={false}
        shape="rectangular"
        logo_alignment="left"
        locale="en"
      />
    </div>
  );
};

const FacebookButton = ({ onSuccess, onFail }) => {
  const fbIcon = (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );

  if (isCapacitor) {
    const handleNativeLogin = async () => {
      try {
        const result = await NativeFacebookLogin.login({
          permissions: ['public_profile', 'email'],
          tracking: 'enabled',
        });
        const token = result?.accessToken?.token;
        if (token) {
          onSuccess({ accessToken: token });
        } else {
          onFail({ status: 'cancelled' });
        }
      } catch (e) {
        console.log('[FB Native] error:', e?.message ?? e);
        onFail({ status: 'failed', error: e });
      }
    };

    return (
      <button
        type="button"
        onClick={handleNativeLogin}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-gray-300 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium transition-colors"
      >
        {fbIcon}
        Continue with Facebook
      </button>
    );
  }

  return (
    <FacebookLogin
      appId={getConfig('FACEBOOK_APP_ID')}
      onSuccess={onSuccess}
      onFail={onFail}
      render={({ onClick }) => (
        <button
          type="button"
          onClick={onClick}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-gray-300 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium transition-colors"
        >
          {fbIcon}
          Continue with Facebook
        </button>
      )}
    />
  );
};

// Sign in with Apple — iOS only; Apple's SDK is not available on Android
const AppleButton = ({ onSuccess, onError }) => {
  if (!isCapacitor || Capacitor.getPlatform() !== 'ios') return null;

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          const result = await nativeAppleSignIn();
          onSuccess(result);
        } catch (e) {
          onError(e);
        }
      }}
      className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg bg-black hover:bg-gray-900 text-white font-medium transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 814 1000" fill="white">
        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.9-49 192.5-49 30.8 0 108.2 2.6 168.4 71.9z"/>
        <path d="M554.1 107.3c23.5-28.2 39.5-67.4 39.5-106.6 0-5.1-.3-10.3-1.2-14.4-37.5 1.3-81.5 24.6-108.8 55.1-21.1 23.5-40.8 62.2-40.8 101.9 0 5.8.9 11.5 1.3 13.4 2.3.3 5.8.6 9.2.6 33.8 0 75.8-22.3 100.8-50z"/>
      </svg>
      Sign in with Apple
    </button>
  );
};

const Divider = ({ theme }) => (
  <div className="flex items-center gap-3">
    <div className={`flex-1 h-px ${theme.border} border-t`} />
    <span className={`text-sm ${theme.textSecondary}`}>or</span>
    <div className={`flex-1 h-px ${theme.border} border-t`} />
  </div>
);

const SignInPage = () => {
  const [step, setStep] = useState('email'); // 'email' | 'method'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authMethods, setAuthMethods] = useState([]); // ['password', 'google', 'facebook']
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendFeedback, setResendFeedback] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [buttonWidth, setButtonWidth] = useState(400);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captcha, setCaptcha] = useState(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [isSuspended, setIsSuspended] = useState(false);
  const timerRef = useRef(null);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    if (!buttonContainerRef.current) return;
    const measure = () => {
      if (buttonContainerRef.current) {
        const style = getComputedStyle(buttonContainerRef.current);
        const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        setButtonWidth(buttonContainerRef.current.offsetWidth - paddingX);
      }
    };
    const observer = new ResizeObserver(measure);
    observer.observe(buttonContainerRef.current);
    measure();
    return () => observer.disconnect();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, googleLogin, facebookLogin, appleLogin, isLoading } = useAuth();
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    if (!email) return;
    const state = getResendState(email);
    setResendCount(state.count);
    if (state.lastSentAt) {
      const remaining = Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - state.lastSentAt)) / 1000);
      setCooldownSeconds(remaining > 0 ? remaining : 0);
    } else {
      setCooldownSeconds(0);
    }
  }, [email]);

  useEffect(() => {
    if (cooldownSeconds <= 0) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setCooldownSeconds(prev => { if (prev <= 1) { clearInterval(timerRef.current); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldownSeconds]);

  useEffect(() => {
    if (location.state?.signupSuccess && location.state?.message) {
      setSuccessMessage(location.state.message);
      const isVerified = location.state.message.toLowerCase().includes('verified');
      setShowResend(!isVerified);
      if (location.state?.redirectAfterLogin) localStorage.setItem('postLoginRedirect', location.state.redirectAfterLogin);
      window.history.replaceState({}, document.title);
    }
    const params = new URLSearchParams(location.search);
    if (params.get('expired') === 'true') {
      setError('Your session has expired due to inactivity. Please sign in again.');
      window.history.replaceState({}, document.title, '/signin');
    }
  }, [location.state, location.search]);

  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('socialUser');
  }, []);

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Home', path: '/' }, { label: 'Sign In' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  const handleRedirectAfterLogin = () => {
    const storedRedirect = localStorage.getItem('postLoginRedirect');
    const fromState = location.state?.from;
    // fromState (ProtectedRoute redirect) is the most current intent — prefer it over stale localStorage
    const redirectUrl = fromState || storedRedirect;
    if (storedRedirect) localStorage.removeItem('postLoginRedirect');
    navigate(redirectUrl || '/');
  };

  // Step 1 — check email
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }
    setError('');
    setCheckingEmail(true);
    try {
      const result = await authAPI.checkEmail(email);
      if (!result.exists) {
        setError('No account found with this email. Please sign up.');
        return;
      }
      setAuthMethods(result.methods);
      setCaptcha(null);
      setCaptchaInput('');
      setIsSuspended(false);
      setStep('method');
    } catch (e) {
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setCheckingEmail(false);
    }
  };

  // Step 2 — password login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!password) { setError('Please enter your password'); return; }

    if (captcha && !captchaInput.trim()) {
      setCaptchaError('Please answer the captcha question.');
      return;
    }

    setError('');
    setCaptchaError('');
    setShowResend(false);
    setResendFeedback('');

    const result = await login(email, password, captcha ? captchaInput.trim() : undefined);
    if (result.success) {
      handleRedirectAfterLogin();
    } else {
      const errMsg = result.error || 'Login failed. Please try again.';

      if (result.code === 'ACCOUNT_SUSPENDED' || errMsg.toLowerCase().includes('suspended')) {
        setIsSuspended(true);
        setError(errMsg);
        return;
      }

      if (result.requiresCaptcha && result.captchaQuestion) {
        setCaptcha({ question: result.captchaQuestion });
        setCaptchaInput('');
        if (result.code === 'CAPTCHA_WRONG') {
          setCaptchaError('Incorrect answer. Please try again.');
        }
      }

      if (result.attemptCount !== undefined) {
        setFailedAttempts(result.attemptCount);
      }

      setError(errMsg);
      if (errMsg.toLowerCase().includes('verify your email')) setShowResend(true);
    }
  };

  // Google login (used in both steps)
  const handleGoogleSuccess = async (tokenResponse) => {
    setError('');
    const result = await googleLogin(tokenResponse.credential);
    if (result.success) {
      const storedRedirect = localStorage.getItem('postLoginRedirect');
      const candidate = storedRedirect || location.state?.from;
      const redirectUrl = isSafeRedirect(candidate) ? candidate : '/';
      if (storedRedirect) localStorage.removeItem('postLoginRedirect');
      if (result.linked) {
        sessionStorage.setItem('linkedAccountMessage', 'Your existing account has been linked with Google. You can now sign in with either Google or your password.');
      }
      // Use full page reload so initAuth picks up the token before rendering protected routes
      window.location.href = redirectUrl;
    } else {
      setError(result.error || 'Google sign-in failed. Please try again.');
    }
  };


  const handleFacebookSuccess = async (response) => {
    setError('');
    const result = await facebookLogin(response.accessToken, response.authenticationToken);
    if (result.success) {
      const storedRedirect = localStorage.getItem('postLoginRedirect');
      const candidate = storedRedirect || location.state?.from;
      const redirectUrl = isSafeRedirect(candidate) ? candidate : '/';
      if (storedRedirect) localStorage.removeItem('postLoginRedirect');
      if (result.linked) {
        sessionStorage.setItem('linkedAccountMessage', 'Your existing account has been linked with Facebook. You can now sign in with either Facebook or your password.');
      }
      window.location.href = redirectUrl;
    } else {
      setError(result.error || 'Facebook sign-in failed. Please try again.');
    }
  };

  const handleAppleSuccess = async ({ identityToken, firstName, lastName }) => {
    setError('');
    const result = await appleLogin(identityToken, firstName, lastName);
    if (result.success) {
      const storedRedirect = localStorage.getItem('postLoginRedirect');
      const candidate = storedRedirect || location.state?.from;
      const redirectUrl = isSafeRedirect(candidate) ? candidate : '/';
      if (storedRedirect) localStorage.removeItem('postLoginRedirect');
      window.location.href = redirectUrl;
    } else {
      setError(result.error || 'Apple Sign-In failed. Please try again.');
    }
  };

  const handleResendVerification = async () => {
    if (!email) { setError('Please enter your email above, then click Resend.'); return; }
    const state = getResendState(email);
    if (state.count >= MAX_RESEND_ATTEMPTS) return;
    setResendLoading(true);
    setResendFeedback('');
    setError('');
    try {
      const res = await fetch('/exportsapi/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        const newCount = state.count + 1;
        saveResendState(email, { count: newCount, lastSentAt: Date.now() });
        setResendCount(newCount);
        setCooldownSeconds(RESEND_COOLDOWN_MS / 1000);
        setResendFeedback(data.message);
      } else {
        setError(data.message || 'Failed to resend verification email.');
      }
    } catch {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const exceededLimit = resendCount >= MAX_RESEND_ATTEMPTS;
  const isCoolingDown = cooldownSeconds > 0;
  const resendDisabled = resendLoading || isCoolingDown || exceededLimit;
  const formatCooldown = (secs) => {
    const m = Math.floor(secs / 60), s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  };

  const ResendBlock = () => (
    <div className={`p-3 rounded-lg border ${theme.border} ${theme.cardHover} space-y-2`}>
      <p className={`text-sm font-medium ${theme.text}`}>Didn't receive the email?</p>
      {exceededLimit ? (
        <p className={`text-sm ${theme.textSecondary}`}>
          Too many attempts. Please{' '}
          <Link to="/help" className={`underline font-medium ${theme.link} ${theme.linkHover}`}>contact us via Help Chat</Link>
          {' '}to resolve your account verification.
        </p>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" onClick={handleResendVerification} disabled={resendDisabled}
            className={`text-sm font-medium px-3 py-1.5 rounded-lg ${theme.buttonSecondary} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}>
            {resendLoading ? 'Sending...' : 'Resend verification email'}
          </button>
          {isCoolingDown && <span className={`text-xs ${theme.textSecondary}`}>available in {formatCooldown(cooldownSeconds)}</span>}
        </div>
      )}
      {resendFeedback && !exceededLimit && <p className={`text-sm ${theme.textSecondary}`}>{resendFeedback}</p>}
    </div>
  );


  return (
    <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-6 px-4 pb-4`}>
      <div ref={buttonContainerRef} className={`${theme.card} rounded-2xl ${theme.shadow} p-8 w-full max-w-md`}>
        <div className="text-center mb-8">
          <Logo size="large" />
          <h2 className={`text-3xl font-bold ${theme.text} mt-6`}>Welcome Back</h2>
          <p className={`${theme.textSecondary} mt-2`}>Sign in to your Kynaix Sports account</p>
        </div>

        {/* ── Step 1: Email ── */}
        {step === 'email' && (
          <form onSubmit={handleCheckEmail} className="space-y-6">
            <GoogleButton onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in was cancelled or failed.')} buttonWidth={buttonWidth} />
            {/* <FacebookButton onSuccess={handleFacebookSuccess} onFail={() => setError('Facebook sign-in was cancelled or failed.')} /> */}
            <AppleButton onSuccess={handleAppleSuccess} onError={() => setError('Apple Sign-In was cancelled or failed.')} />
            <Divider theme={theme} />
            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Email Address</label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
                placeholder="your@email.com" required autoComplete="email"
                className={`w-full px-4 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`} />
            </div>

            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">{successMessage}</p>
                {showResend && <ResendBlock />}
              </div>
            )}
            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-sm text-red-600">{error}</p></div>}
            {showResend && error && <ResendBlock />}

            <button type="submit" disabled={checkingEmail}
              className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}>
              {checkingEmail ? 'Checking...' : 'Continue'}
            </button>
          </form>
        )}

        {/* ── Step 2: Auth method ── */}
        {step === 'method' && (
          <div className="space-y-6">
            {/* Email display + back */}
            <div className={`flex items-center justify-between p-3 rounded-lg border ${theme.border} ${theme.cardHover}`}>
              <span className={`text-sm ${theme.text}`}>{email}</span>
              <button type="button" onClick={() => { setStep('email'); setError(''); setPassword(''); }}
                className={`text-sm ${theme.link} ${theme.linkHover} font-medium`}>Change</button>
            </div>

            {/* Social only (Google or Facebook, no password) */}
            {!authMethods.includes('password') && (authMethods.includes('google') || authMethods.includes('facebook')) && (
              <div className="space-y-4">
                <p className={`text-sm text-center ${theme.textSecondary}`}>
                  This account was created with {authMethods.includes('google') && authMethods.includes('facebook') ? 'a social account' : authMethods.includes('google') ? 'Google' : 'Facebook'}. Please sign in below.
                </p>
                {authMethods.includes('google') && <GoogleButton onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in was cancelled or failed.')} buttonWidth={buttonWidth} text="signin_with" />}
                {/* authMethods.includes('facebook') && <FacebookButton onSuccess={handleFacebookSuccess} onFail={() => setError('Facebook sign-in was cancelled or failed.')} /> */}
                {authMethods.includes('apple') && <AppleButton onSuccess={handleAppleSuccess} onError={() => setError('Apple Sign-In was cancelled or failed.')} />}
              </div>
            )}

            {/* Password only */}
            {authMethods.includes('password') && !authMethods.includes('google') && !authMethods.includes('facebook') && (
              <form onSubmit={handlePasswordLogin} className="space-y-6">
                {/* Hidden email field — iOS uses this to associate username with password for Keychain/autofill */}
                <input type="email" value={email} autoComplete="username" readOnly style={{ display: 'none' }} />
                {isSuspended ? (
                  <div className="p-4 bg-red-50 border border-red-300 rounded-lg space-y-2">
                    <p className="text-sm font-semibold text-red-700">Account Suspended</p>
                    <p className="text-sm text-red-600">{error}</p>
                    <Link to="/forgot-password" className={`text-sm font-medium underline ${theme.link} ${theme.linkHover}`}>Reset your password to reactivate</Link>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Password</label>
                      <div className="relative">
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                          placeholder="Enter your password" required autoComplete="current-password"
                          className={`w-full px-4 pr-10 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`} />
                        <button type="button" onClick={() => setShowPassword(v => !v)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Link to="/forgot-password" className={`text-sm ${theme.link} ${theme.linkHover}`}>Forgot password?</Link>
                    </div>
                    {captcha && (
                      <div className={`p-3 rounded-lg border ${theme.border} space-y-2`}>
                        <p className={`text-sm font-medium ${theme.text}`}>Security check: What is {captcha.question}?</p>
                        <input type="text" value={captchaInput} onChange={e => { setCaptchaInput(e.target.value); setCaptchaError(''); }}
                          placeholder="Your answer" inputMode="numeric"
                          className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`} />
                        {captchaError && <p className="text-xs text-red-500">{captchaError}</p>}
                      </div>
                    )}
                    {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-sm text-red-600">{error}</p></div>}
                    {showResend && <ResendBlock />}
                    <button type="submit" disabled={isLoading}
                      className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}>
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </button>
                  </>
                )}
              </form>
            )}

            {/* Password + social (Google and/or Facebook linked) */}
            {authMethods.includes('password') && (authMethods.includes('google') || authMethods.includes('facebook')) && (
              <div className="space-y-4">
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  {/* Hidden email field — iOS uses this to associate username with password for Keychain/autofill */}
                  <input type="email" value={email} autoComplete="username" readOnly style={{ display: 'none' }} />
                  {isSuspended ? (
                    <div className="p-4 bg-red-50 border border-red-300 rounded-lg space-y-2">
                      <p className="text-sm font-semibold text-red-700">Account Suspended</p>
                      <p className="text-sm text-red-600">{error}</p>
                      <Link to="/forgot-password" className={`text-sm font-medium underline ${theme.link} ${theme.linkHover}`}>Reset your password to reactivate</Link>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Password</label>
                        <div className="relative">
                          <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                            placeholder="Enter your password" required autoComplete="current-password"
                            className={`w-full px-4 pr-10 py-3 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`} />
                          <button type="button" onClick={() => setShowPassword(v => !v)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Link to="/forgot-password" className={`text-sm ${theme.link} ${theme.linkHover}`}>Forgot password?</Link>
                      </div>
                      {captcha && (
                        <div className={`p-3 rounded-lg border ${theme.border} space-y-2`}>
                          <p className={`text-sm font-medium ${theme.text}`}>Security check: What is {captcha.question}?</p>
                          <input type="text" value={captchaInput} onChange={e => { setCaptchaInput(e.target.value); setCaptchaError(''); }}
                            placeholder="Your answer" inputMode="numeric"
                            className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`} />
                          {captchaError && <p className="text-xs text-red-500">{captchaError}</p>}
                        </div>
                      )}
                      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-sm text-red-600">{error}</p></div>}
                      {showResend && <ResendBlock />}
                      <button type="submit" disabled={isLoading}
                        className={`w-full ${theme.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed`}>
                        {isLoading ? 'Signing In...' : 'Sign In'}
                      </button>
                    </>
                  )}
                </form>
                <Divider theme={theme} />
                {authMethods.includes('google') && <GoogleButton onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in was cancelled or failed.')} buttonWidth={buttonWidth} text="signin_with" />}
                {/* authMethods.includes('facebook') && <FacebookButton onSuccess={handleFacebookSuccess} onFail={() => setError('Facebook sign-in was cancelled or failed.')} /> */}
                <AppleButton onSuccess={handleAppleSuccess} onError={() => setError('Apple Sign-In was cancelled or failed.')} />
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <span className={`text-sm ${theme.textSecondary}`}>Don't have an account?</span>
            <Link to="/signup" className={`text-sm font-semibold px-4 py-1.5 rounded-lg border ${theme.border} ${theme.text} ${theme.cardHover} transition-colors`}>Sign up</Link>
          </div>
          {!isCapacitor && (
            <div className="flex flex-col items-center gap-2">
              <span className={`text-xs ${theme.textMuted}`}>Or get the app</span>
              <StoreButtons size="sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
